import React, { useEffect, useState } from "react";
import PLAN_URL from "../../../utils/PLAN_URL";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Snackbar,
  Grid,
  Box,
  Tooltip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { motion } from "framer-motion";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import EditIcon from "@mui/icons-material/Edit";
import SpeedIcon from "@mui/icons-material/Speed";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import * as S from "./styles";

function CustomSettings() {
  const { group_code, project_code } = useParams();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const settingsInfo = {
    pce: {
      title: "PCE (Process Cycle Efficiency)",
      description:
        "생산 공정의 효율성을 나타내는 값입니다. 값이 클수록 높은 효율을 의미합니다.",
      icon: <SpeedIcon sx={{ fontSize: 28 }} />,
      color: "#4caf50",
      unit: "%",
      min: 0,
      max: 100,
    },
    lot_size: {
      title: "LOT 크기",
      description: "생산 단위당 처리되는 수량을 나타냅니다.",
      icon: <InventoryIcon sx={{ fontSize: 28 }} />,
      color: "#2196f3",
      unit: "EA",
      min: 1,
      max: 1000,
    },
    end_margin_hours: {
      title: "종료 여유 시간",
      description: "계획된 작업 종료 후 추가적으로 확보할 여유 시간입니다.",
      icon: <AccessTimeIcon sx={{ fontSize: 28 }} />,
      color: "#ff9800",
      unit: "초",
      min: 0,
      max: 3600,
    },
    alignment_option: {
      title: "정렬 옵션",
      description:
        "계획 시작(Start) 또는 종료(End) 기준으로 정렬하는 옵션입니다.",
      icon: <CompareArrowsIcon sx={{ fontSize: 28 }} />,
      color: "#9c27b0",
      options: ["End", "Start"],
    },
  };
  useEffect(() => {
    fetchSettings();
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, [group_code, project_code]);
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        PLAN_URL +
          `/api/admin/getCustomSettings?group_code=${group_code}&project_code=${project_code}`
      );

      if (res.data && Array.isArray(res.data)) {
        setSettings(res.data);
        setOriginalSettings(JSON.parse(JSON.stringify(res.data)));
      } else {
        console.error("Invalid data format:", res.data);
        openSnackbar("데이터 형식이 올바르지 않습니다.", "error");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      openSnackbar("설정을 불러오는 중 오류가 발생했습니다.", "error");
    } finally {
      setLoading(false);
    }
  };
  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  const handleToggleEditMode = () => {
    if (editMode) {
      setSettings(JSON.parse(JSON.stringify(originalSettings)));
    } else {
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
    }
    setEditMode(!editMode);
  };
  const handleValueChange = (name, value) => {
    setSettings((prevSettings) =>
      prevSettings.map((setting) =>
        setting.property_name === name
          ? { ...setting, property_value: value }
          : setting
      )
    );
  };
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        PLAN_URL + "/api/admin/updateCustomSettings",
        {
          group_code,
          project_code,
          settings: settings.map((setting) => ({
            property_name: setting.property_name,
            property_value: setting.property_value,
          })),
        }
      );
      if (res.status === 200) {
        openSnackbar("설정이 성공적으로 저장되었습니다.", "success");
        fetchSettings();
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      openSnackbar("설정 저장 중 오류가 발생했습니다.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    openSnackbar("변경사항이 취소되었습니다.", "info");
  };

  const isSettingsChanged = () => {
    if (!originalSettings.length || !settings.length) return false;

    return settings.some(
      (setting, index) =>
        setting.property_value !== originalSettings[index].property_value
    );
  };

  const renderSettingCard = (setting) => {
    const info = settingsInfo[setting.property_name];
    if (!info) return null;
    return (
      <S.StyledCard>
        <S.CardHeader>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                bgcolor: info.color,
                color: "white",
                p: 1,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {info.icon}
            </Box>
            <S.Title variant="h6">{info.title}</S.Title>
          </Box>
        </S.CardHeader>
        <Box sx={{ p: 3 }}>
          <S.InfoItem>
            <S.IconWrapper>{info.icon}</S.IconWrapper>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <S.InfoText>현재 값: </S.InfoText>
              {setting.property_name === "alignment_option" ? (
                editMode ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={setting.property_value === 1}
                        onChange={(e) =>
                          handleValueChange(
                            setting.property_name,
                            e.target.checked ? 1 : 0
                          )
                        }
                        color="primary"
                      />
                    }
                    label={setting.property_value === 1 ? "Start" : "End"}
                  />
                ) : (
                  <S.ValueText>
                    {setting.property_value === 1 ? "Start" : "End"}
                  </S.ValueText>
                )
              ) : editMode ? (
                <TextField
                  fullWidth
                  type="number"
                  value={setting.property_value}
                  onChange={(e) =>
                    handleValueChange(
                      setting.property_name,
                      parseInt(e.target.value) || 0
                    )
                  }
                  variant="outlined"
                  size="small"
                  margin="normal"
                  InputProps={{
                    endAdornment: info.unit ? (
                      <InputAdornment position="end">
                        {info.unit}
                      </InputAdornment>
                    ) : null,
                  }}
                  inputProps={{
                    min: info.min,
                    max: info.max,
                  }}
                />
              ) : (
                <S.ValueText>
                  {setting.property_value}
                  {info.unit && ` ${info.unit}`}
                </S.ValueText>
              )}
            </Box>
          </S.InfoItem>
          <Box sx={{ mt: 2 }}>
            <S.InfoText>설명: </S.InfoText>
            <S.InfoText>{info.description}</S.InfoText>
          </Box>
          <Box sx={{ mt: 3, fontSize: "0.75rem", color: "text.secondary" }}>
            최종 업데이트: {new Date(setting.update_time).toLocaleString()}
          </Box>
        </Box>
      </S.StyledCard>
    );
  };

  return (
    <S.PageContainer>
      <S.HeaderContainer>
        <S.Title
          variant="h5"
          fontWeight="bold"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: "22px !important",
          }}
        >
          AI 고급 설정 &nbsp;
          {/* <Tooltip
            title="도움말"
            sx={{ cursor: "pointer" }}
            onClick={() => goToHelpLink("https://help.ese-tech.com/ko/planner")}
          >
            <HelpOutlineIcon />
          </Tooltip> */}
        </S.Title>
        <S.TopBtnBox>
          {editMode ? (
            <>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<RestartAltIcon />}
                onClick={handleReset}
                disabled={!isSettingsChanged() || loading || saving}
              >
                취소
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveSettings}
                disabled={!isSettingsChanged() || loading || saving}
              >
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "저장"
                )}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleToggleEditMode}
              disabled={loading}
            >
              설정 수정
            </Button>
          )}
        </S.TopBtnBox>
      </S.HeaderContainer>

      <S.ContentContainer>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ height: "calc(100% - 24px)" }}>
            {settings.map((setting, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                key={setting.property_name}
                sx={{ height: "50%" }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: mounted ? 1 : 0, y: mounted ? 0 : 20 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  style={{ height: "100%" }}
                >
                  {renderSettingCard(setting)}
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </S.ContentContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </S.PageContainer>
  );
}

export default CustomSettings;
